/* eslint-disable  */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import { ScanLine, ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, Camera, CameraOff } from "lucide-react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { inventoryActionCreators } from "../../services/Actions";
import RenderInventoryPage from "../RenderInventoryPage";

const MODES = [
  { key: "RECEIVE", label: "Receive (Stock In)", icon: <ArrowDownToLine size={16} />, active: "bg-emerald-600 text-white border-emerald-600 shadow" },
  { key: "DISPATCH", label: "Dispatch (Stock Out)", icon: <ArrowUpFromLine size={16} />, active: "bg-rose-600 text-white border-rose-600 shadow" },
  { key: "COUNT", label: "Count (Stock Take)", icon: <ClipboardCheck size={16} />, active: "bg-blue-600 text-white border-blue-600 shadow" },
];

function ScanStation() {
  const dispatch = useDispatch();
  const { ScanItem, GetStockTakes, ResetInventoryResponse } = bindActionCreators(
    inventoryActionCreators,
    dispatch
  );
  const inventory = useSelector((state) => state.inventory);
  const { response, scan_result, stock_takes } = inventory;

  const [mode, setMode] = useState("RECEIVE");
  const [qty, setQty] = useState(1);
  const [barcode, setBarcode] = useState("");
  const [stockTakeId, setStockTakeId] = useState("");
  const [history, setHistory] = useState([]);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const inputRef = useRef(null);

  // Keep the latest values in refs so the camera callback never uses stale state.
  const modeRef = useRef(mode);
  const qtyRef = useRef(qty);
  const stockTakeRef = useRef(stockTakeId);
  const lastScanRef = useRef({ code: "", at: 0 });
  const scannerRef = useRef(null);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { qtyRef.current = qty; }, [qty]);
  useEffect(() => { stockTakeRef.current = stockTakeId; }, [stockTakeId]);

  const openSessions = (stock_takes || []).filter((s) => s.status === "open");

  useEffect(() => {
    GetStockTakes();
    inputRef.current?.focus();
    return () => stopCamera();
  }, []);

  // Shared submit used by both the keyboard input and the camera.
  const submitScan = (code) => {
    const value = (code || "").trim();
    if (!value) return;
    const m = modeRef.current;
    if (m === "COUNT" && !stockTakeRef.current) {
      toast.error("Select an open stock-take session first");
      return;
    }
    ScanItem({
      barcode: value,
      mode: m,
      qty: Number(qtyRef.current) || 1,
      ...(m === "COUNT" ? { stock_take_id: stockTakeRef.current } : {}),
    });
  };

  const handleScan = (e) => {
    e.preventDefault();
    submitScan(barcode);
    setBarcode("");
    inputRef.current?.focus();
  };

  // ── Phone / webcam camera scanning ──
  const onCameraDecode = (decodedText) => {
    const now = Date.now();
    // Ignore the same code re-read within 2.5s (the camera reads continuously).
    if (
      decodedText === lastScanRef.current.code &&
      now - lastScanRef.current.at < 2500
    ) {
      return;
    }
    lastScanRef.current = { code: decodedText, at: now };
    submitScan(decodedText);
  };

  const startCamera = async () => {
    setCameraError("");
    setCameraOn(true); // reveal the reader element so the library can size it
    try {
      const scanner = new Html5Qrcode("inv-reader", {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.QR_CODE,
        ],
        verbose: false,
      });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" }, // back camera on phones
        { fps: 10, qrbox: { width: 260, height: 160 } },
        onCameraDecode,
        () => {} // ignore per-frame "not found" errors
      );
      setCameraOn(true);
    } catch (err) {
      setCameraError(
        err?.message?.includes("NotAllowed") || err?.name === "NotAllowedError"
          ? "Camera permission was denied. Allow camera access and try again."
          : "Could not start the camera. Make sure the site is opened over HTTPS."
      );
      setCameraOn(false);
    }
  };

  const stopCamera = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setCameraOn(false);
    if (scanner) {
      try {
        await scanner.stop();
        await scanner.clear();
      } catch (_) {}
    }
  };

  const toggleCamera = () => {
    if (cameraOn) stopCamera();
    else startCamera();
  };

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setHistory((h) => [
        { at: new Date(), mode, barcode: scan_result?.sku || scan_result?.barcode, on_hand: scan_result?.stock_on_hand },
        ...h,
      ].slice(0, 15));
      setTimeout(() => ResetInventoryResponse(), 1000);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetInventoryResponse(), 1500);
    }
  }, [response]);

  return (
    <RenderInventoryPage title={"Scan Station"}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scan panel */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          {/* Mode selector */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {MODES.map((m) => (
              <button
                key={m.key}
                onClick={() => { setMode(m.key); inputRef.current?.focus(); }}
                className={`flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold border transition-all ${mode === m.key ? m.active : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"}`}
              >
                {m.icon} <span className="hidden sm:inline">{m.label}</span>
              </button>
            ))}
          </div>

          {mode === "COUNT" && (
            <div className="mb-4">
              <label className="block mb-2 text-sm font-bold text-gray-700">Open Stock-Take Session *</label>
              <select
                value={stockTakeId}
                onChange={(e) => setStockTakeId(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
              >
                <option value="">Select session…</option>
                {openSessions.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.location || "Session"} — {new Date(s.created_at).toLocaleString()}
                  </option>
                ))}
              </select>
              {openSessions.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No open session. Start one under Stock Take.</p>
              )}
            </div>
          )}

          <form onSubmit={handleScan}>
            <label className="block mb-2 text-sm font-bold text-gray-700">Scan or type barcode</label>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <ScanLine size={20} className="absolute left-3 top-3.5 text-emerald-500" />
                <input
                  ref={inputRef}
                  autoFocus
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder="Point the scanner here and scan…"
                  className="pl-11 pr-3 py-3 text-lg font-mono border-2 border-emerald-200 rounded-xl w-full focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-24 py-3 text-center text-lg border-2 border-gray-200 rounded-xl"
                title="Quantity per scan"
              />
              <button type="submit" className="px-6 py-3 text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl font-semibold shadow">Submit</button>
            </div>
            <p className="text-xs text-gray-400 mt-2">A hardware scanner sends an Enter key after each code, so it submits automatically.</p>
          </form>

          {/* Phone / webcam camera scanning */}
          <div className="mt-6 border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-gray-700">Scan with phone camera</p>
                <p className="text-xs text-gray-400">No hardware scanner? Use this device's camera. (Needs an HTTPS connection.)</p>
              </div>
              <button
                type="button"
                onClick={toggleCamera}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm shadow ${cameraOn ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"}`}
              >
                {cameraOn ? <CameraOff size={16} /> : <Camera size={16} />}
                {cameraOn ? "Stop Camera" : "Use Camera"}
              </button>
            </div>

            {/* The camera renders into this element when active */}
            <div className={`mt-4 ${cameraOn ? "block" : "hidden"}`}>
              <div id="inv-reader" className="w-full max-w-md mx-auto rounded-xl overflow-hidden border-2 border-emerald-300" />
              <p className="text-xs text-center text-gray-500 mt-2">
                Point the back camera at the barcode — it submits automatically in <span className="font-semibold">{mode}</span> mode.
              </p>
            </div>

            {cameraError && (
              <p className="mt-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">{cameraError}</p>
            )}
          </div>

          {scan_result && (
            <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-sm text-emerald-800">
                Last: <span className="font-bold">{scan_result?.sku}</span> — on hand now <span className="font-bold">{scan_result?.stock_on_hand}</span>
              </p>
            </div>
          )}
        </div>

        {/* Recent scans */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-4">Recent Scans</h3>
          {history.length === 0 ? (
            <p className="text-sm text-gray-400">Scans will appear here.</p>
          ) : (
            <ul className="space-y-2">
              {history.map((h, idx) => (
                <li key={idx} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                  <div>
                    <span className="font-mono font-semibold">{h.barcode}</span>
                    <span className="ml-2 text-xs text-gray-400">{h.mode}</span>
                  </div>
                  <span className="text-xs text-gray-500">on hand: {h.on_hand}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ToastContainer autoClose={2000} />
    </RenderInventoryPage>
  );
}

export default ScanStation;
